import { useState, useEffect } from "react";
import axios from "axios";
import { getApiHeaders } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import { BASE_URL } from "../api";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import MessageToster from "../component/MessageToster";
import { useNavigate, Link } from "react-router-dom";
import { useRealAccounts } from "../hooks/useRealAccounts";
import { useAccountBalance } from "../hooks/useAccountBalance";
import { useUser } from "../context/UserContext";
import CommonTable from "../component/CommonTable";

/**
 * FEATURE TOGGLE:
 * Set ENABLE_USER_TO_USER_TRANSFER = false or comment out the User-to-User section below 
 * if you want to disable User to User transfer in the future.
 */
const ENABLE_USER_TO_USER_TRANSFER = true;

export default function Transfer() {
  const { userData, fetchUserData } = useUser();
  const handleError = useHandleError();
  const navigate = useNavigate();
  const { accounts, loadingAccounts } = useRealAccounts();
  const [loading, setLoading] = useState(false);

  // Tab State: "mt5" (Wallet to MT5 Account) | "mt5_to_wallet" (MT5 to Wallet) | "user" (User to User Transfer)
  const [activeTab, setActiveTab] = useState("mt5");

  // --- MT5 Transfer State (Wallet to MT5) ---
  const [mt5FormData, setMt5FormData] = useState({
    account_number: "",
    amount: "",
    password: "",
  });
  const [mt5Errors, setMt5Errors] = useState({});
  const [showMt5Password, setShowMt5Password] = useState(false);
  const { balance, loadingBalance } = useAccountBalance(mt5FormData.account_number);

  // --- MT5 to Wallet Transfer State ---
  const [mt5ToWalletFormData, setMt5ToWalletFormData] = useState({
    account_number: "",
    amount: "",
    password: "",
  });
  const [mt5ToWalletErrors, setMt5ToWalletErrors] = useState({});
  const [showMt5ToWalletPassword, setShowMt5ToWalletPassword] = useState(false);
  // Only load this balance while the MT5 to Wallet tab is open, otherwise the same API is called twice on page load
  const { balance: mt5ToWalletBalance, loadingBalance: loadingMt5ToWalletBalance } = useAccountBalance(activeTab === "mt5_to_wallet" ? mt5ToWalletFormData.account_number : "");

  // --- User to User Transfer State ---
  const [userFormData, setUserFormData] = useState({
    receiver_email: "",
    amount: "",
    password: "",
    description: "",
  });
  const [userErrors, setUserErrors] = useState({});
  const [showUserPassword, setShowUserPassword] = useState(false);

  // --- Security Verification (OTP) Modal State ---
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);

  // Set default account when accounts list is loaded for MT5 transfers
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const firstAcc = String(accounts[0]);
      if (!mt5FormData.account_number || (!accounts.includes(Number(mt5FormData.account_number)) && !accounts.includes(String(mt5FormData.account_number)))) {
        setMt5FormData((prev) => ({ ...prev, account_number: firstAcc }));
      }
      if (!mt5ToWalletFormData.account_number || (!accounts.includes(Number(mt5ToWalletFormData.account_number)) && !accounts.includes(String(mt5ToWalletFormData.account_number)))) {
        setMt5ToWalletFormData((prev) => ({ ...prev, account_number: firstAcc }));
      }
    } else {
      setMt5FormData((prev) => ({ ...prev, account_number: "" }));
      setMt5ToWalletFormData((prev) => ({ ...prev, account_number: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  // OTP Countdown timer
  useEffect(() => {
    let timer;
    if (showOtpModal && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showOtpModal, timeLeft]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Auto submit when 6 digit OTP is filled
  useEffect(() => {
    if (otp.length === 6 && showOtpModal && !otpLoading) {
      const timer = setTimeout(() => {
        handleConfirmTransfer();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, showOtpModal]);

  // --- MT5 Form Handlers ---
  const handleMt5InputChange = (e) => {
    let { name, value, type } = e.target;
    if (type === 'number' && Number(value) < 0) {
      value = Math.abs(value).toString();
    }
    if (name === "account_number") {
      value = String(value).trim();
    }
    if (name === "amount") {
      value = String(value).replace(/[^0-9.]/g, "");
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    setMt5FormData((prev) => ({ ...prev, [name]: value }));
    if (mt5Errors[name]) {
      setMt5Errors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBeforeMt5Transfer = async (e) => {
    e.preventDefault();

    let errors = {};

    if (!mt5FormData.account_number) {
      errors.account_number = "MT5 Live Account is required. Please create an account first.";
    }

    if (!mt5FormData.password.trim()) {
      errors.password = "MT5 Trading password is required.";
    }

    const amountNum = parseFloat(mt5FormData.amount);
    if (!mt5FormData.amount) {
      errors.amount = "Amount is required.";
    } else if (isNaN(amountNum) || amountNum < 100 || amountNum > 10000000) {
      errors.amount = "Amount must be between $100 and $10,000,000.";
    } else if (userData?.commissionwallet !== undefined && amountNum > Number(userData.commissionwallet)) {
      errors.amount = `Insufficient Income Wallet balance ($${Number(userData.commissionwallet).toFixed(2)}).`;
    } 
    // else if (userData?.pinwallet !== undefined && amountNum > Number(userData.pinwallet)) {
    //   errors.amount = `Insufficient Fund Wallet balance ($${Number(userData.pinwallet).toFixed(2)}).`;
    // }

    if (Object.keys(errors).length > 0) {
      setMt5Errors(errors);
      return;
    }

    setLoading(true);
    const payload = {
      account: String(mt5FormData.account_number).trim(),
      amount: amountNum,
      password: mt5FormData.password.trim(),
      // domain: window.location.origin,
    };

    try {
      const res = await axios.post(`${BASE_URL}/internal_transfer/before_internal_transfer`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
        MessageToster("success", res.data?.message || "OTP sent successfully.");
        setShowOtpModal(true);
        setOtp("");
        setTimeLeft(120);
      } else {
        MessageToster("error", res.data?.message || "Validation failed.");
      }
    } catch (error) {
      console.error("Before MT5 transfer error:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // --- MT5 to Wallet Form Handlers ---
  const handleMt5ToWalletInputChange = (e) => {
    let { name, value, type } = e.target;
    if (type === 'number' && Number(value) < 0) {
      value = Math.abs(value).toString();
    }
    if (name === "account_number") {
      value = String(value).trim();
    }
    if (name === "amount") {
      value = String(value).replace(/[^0-9.]/g, "");
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    setMt5ToWalletFormData((prev) => ({ ...prev, [name]: value }));
    if (mt5ToWalletErrors[name]) {
      setMt5ToWalletErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBeforeMt5ToWalletTransfer = async (e) => {
    e.preventDefault();

    let errors = {};

    if (!mt5ToWalletFormData.account_number) {
      errors.account_number = "MT5 Live Account is required. Please create an account first.";
    }

    if (!mt5ToWalletFormData.password.trim()) {
      errors.password = "MT5 Trading password is required.";
    }

    const amountNum = parseFloat(mt5ToWalletFormData.amount);
    if (!mt5ToWalletFormData.amount) {
      errors.amount = "Amount is required.";
    } else if (isNaN(amountNum) || amountNum <= 0 || amountNum > 10000000) {
      errors.amount = "Amount must be greater than $0 and up to $10,000,000.";
    } else if (mt5ToWalletBalance !== null && !loadingMt5ToWalletBalance && amountNum > Number(mt5ToWalletBalance)) {
      errors.amount = `Insufficient MT5 account balance ($${Number(mt5ToWalletBalance).toFixed(2)}).`;
    }

    if (Object.keys(errors).length > 0) {
      setMt5ToWalletErrors(errors);
      return;
    }

    setLoading(true);
    const payload = {
      account: String(mt5ToWalletFormData.account_number).trim(),
      password: mt5ToWalletFormData.password.trim(),
      amount: amountNum,
    };

    try {
      const res = await axios.post(`${BASE_URL}/internal_transfer/before_handle_transfer`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
        MessageToster("success", res.data?.message || res.data?.msg || "OTP sent successfully.");
        setShowOtpModal(true);
        setOtp("");
        setTimeLeft(120);
      } else {
        MessageToster("error", res.data?.message || res.data?.msg || "Validation failed.");
      }
    } catch (error) {
      console.error("Before MT5 to Wallet transfer error:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================================
     USER TO USER TRANSFER LOGIC (CAN BE COMMENTED OUT OR TOGGLED AS NEEDED)
     ========================================================================= */
  const handleUserInputChange = (e) => {
    let { name, value, type } = e.target;
    if (type === 'number' && Number(value) < 0) {
      value = Math.abs(value).toString();
    }
    if (name === "receiver_email") {
      value = String(value).trim();
    }
    if (name === "amount") {
      value = String(value).replace(/[^0-9.]/g, "");
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    setUserFormData((prev) => ({ ...prev, [name]: value }));
    if (userErrors[name]) {
      setUserErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBeforeUserTransfer = async (e) => {
    e.preventDefault();

    let errors = {};

    if (!userFormData.receiver_email.trim()) {
      errors.receiver_email = "Receiver email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userFormData.receiver_email.trim())) {
      errors.receiver_email = "Please enter a valid email address.";
    } else if (userData?.email && userFormData.receiver_email.trim().toLowerCase() === userData.email.toLowerCase()) {
      errors.receiver_email = "You cannot transfer funds to your own account.";
    }

    if (!userFormData.password.trim()) {
      errors.password = "Account password is required.";
    }

    const amountNum = parseFloat(userFormData.amount);
    if (!userFormData.amount) {
      errors.amount = "Amount is required.";
    } else if (isNaN(amountNum) || amountNum < 10 || amountNum > 10000000) {
      errors.amount = "Amount must be between $10 and $10,000,000.";
    } else if (userData?.commissionwallet !== undefined && amountNum > Number(userData.commissionwallet)) {
      errors.amount = `Insufficient Income Wallet balance ($${Number(userData.commissionwallet).toFixed(2)}).`;
    }
    //  else if (userData?.pinwallet !== undefined && amountNum > Number(userData.pinwallet)) {
    //   errors.amount = `Insufficient Fund Wallet balance ($${Number(userData.pinwallet).toFixed(2)}).`;
    // }

    if (Object.keys(errors).length > 0) {
      setUserErrors(errors);
      return;
    }

    setLoading(true);
    const payload = {
      password: userFormData.password.trim(),
      receiver_email: userFormData.receiver_email.trim(),
      amount: amountNum,
      description: userFormData.description.trim(),
    };

    try {
      const res = await axios.post(`${BASE_URL}/internal_transfer/before_internal_transfer_user`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
        MessageToster("success", res.data?.message || "OTP sent successfully.");
        setShowOtpModal(true);
        setOtp("");
        setTimeLeft(120);
      } else {
        MessageToster("error", res.data?.message || "Validation failed.");
      }
    } catch (error) {
      console.error("Before user-to-user transfer error:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // --- Resend OTP Handler (Works for MT5, MT5 to Wallet, & User to User Transfer) ---
  const handleResendOtp = async () => {
    if (timeLeft > 0) return;

    setOtp("");
    setTimeLeft(120);

    try {
      if (activeTab === "mt5") {
        const amountNum = parseFloat(mt5FormData.amount);
        const payload = {
          account: String(mt5FormData.account_number).trim(),
          account_number: String(mt5FormData.account_number).trim(),
          amount: amountNum,
          password: mt5FormData.password.trim(),
        };
        const res = await axios.post(`${BASE_URL}/internal_transfer/before_internal_transfer`, payload, {
          headers: getApiHeaders(),
        });
        if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
          MessageToster("success", res.data?.message || "OTP resent successfully.");
        } else {
          MessageToster("error", res.data?.message || "Failed to resend OTP.");
          setTimeLeft(0);
        }
      } else if (activeTab === "mt5_to_wallet") {
        const amountNum = parseFloat(mt5ToWalletFormData.amount);
        const payload = {
          account: String(mt5ToWalletFormData.account_number).trim(),
          password: mt5ToWalletFormData.password.trim(),
          amount: amountNum,
        };
        const res = await axios.post(`${BASE_URL}/internal_transfer/before_handle_transfer`, payload, {
          headers: getApiHeaders(),
        });
        if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
          MessageToster("success", res.data?.message || res.data?.msg || "OTP resent successfully.");
        } else {
          MessageToster("error", res.data?.message || res.data?.msg || "Failed to resend OTP.");
          setTimeLeft(0);
        }
      } else {
        /* User to User OTP Resend */
        const amountNum = parseFloat(userFormData.amount);
        const payload = {
          password: userFormData.password.trim(),
          receiver_email: userFormData.receiver_email.trim(),
          amount: amountNum,
          description: userFormData.description.trim(),
        };
        const res = await axios.post(`${BASE_URL}/internal_transfer/before_internal_transfer_user`, payload, {
          headers: getApiHeaders(),
        });
        if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
          MessageToster("success", res.data?.message || "OTP resent successfully.");
        } else {
          MessageToster("error", res.data?.message || "Failed to resend OTP.");
          setTimeLeft(0);
        }
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      handleError(error);
      setTimeLeft(0);
    }
  };

  // --- Confirm Transfer Handler (Works for MT5, MT5 to Wallet, & User to User Transfer) ---
  const handleConfirmTransfer = async () => {
    if (otp.length !== 6) {
      MessageToster("error", "Please enter the 6-digit OTP.");
      return;
    }

    setOtpLoading(true);

    try {
      if (activeTab === "mt5") {
        // MT5 Transfer Confirmation (Wallet to MT5)
        const amountNum = parseFloat(mt5FormData.amount);
        const payload = {
          account_number: String(mt5FormData.account_number).trim(),
          amount: amountNum,
          password: mt5FormData.password.trim(),
          otp: otp.trim(),
          domain: window.location.origin,
        };

        const res = await axios.post(`${BASE_URL}/after_transfer`, payload, {
          headers: getApiHeaders(),
        });

        if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
          MessageToster("success", res.data?.message || "Transfer Successful!");
          setShowOtpModal(false);
          setMt5FormData((prev) => ({ ...prev, amount: "", password: "" }));
          setOtp("");
          if (fetchUserData) fetchUserData();
          navigate("/account_transaction?type=mt5");
        } else {
          MessageToster("error", res.data?.message || "Transfer failed.");
        }
      } else if (activeTab === "mt5_to_wallet") {
        // MT5 to Wallet Confirmation
        const amountNum = parseFloat(mt5ToWalletFormData.amount);
        const payload = {
          account_number: String(mt5ToWalletFormData.account_number).trim(),
          otp: String(otp).trim(),
          amount: amountNum,
          password: mt5ToWalletFormData.password.trim(),
        };

        const res = await axios.post(`${BASE_URL}/internal_transfer/balance_handle_transfer`, payload, {
          headers: getApiHeaders(),
        });

        if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
          MessageToster("success", res.data?.message || res.data?.msg || "Transfer Successful!");
          setShowOtpModal(false);
          setMt5ToWalletFormData((prev) => ({ ...prev, amount: "", password: "" }));
          setOtp("");
          if (fetchUserData) fetchUserData();
          navigate("/account_transaction?type=mt5");
        } else {
          MessageToster("error", res.data?.message || res.data?.msg || "Transfer failed.");
        }
      } else {
        // User to User Transfer Confirmation
        const amountNum = parseFloat(userFormData.amount);
        const payload = {
          password: userFormData.password.trim(),
          receiver_email: userFormData.receiver_email.trim(),
          amount: amountNum,
          verifycode: parseInt(otp.trim(), 10),
          description: userFormData.description.trim(),
        };

        const res = await axios.post(`${BASE_URL}/internal_transfer/internal_transfer`, payload, {
          headers: getApiHeaders(),
        });

        if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
          MessageToster("success", res.data?.message || res.data?.msg || "Transfer Successful!");
          setShowOtpModal(false);
          setUserFormData({ receiver_email: "", amount: "", password: "", description: "" });
          setOtp("");
          if (fetchUserData) fetchUserData();
          navigate("/account_transaction?type=user");
        } else {
          MessageToster("error", res.data?.message || res.data?.msg || "Transfer failed.");
        }
      }
    } catch (error) {
      console.error("After transfer error:", error);
      handleError(error);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* Page Title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-0">Transfer</h2>
                    <div className="page-title-right">
                      {activeTab === "user" ? (
                        <button
                          className="btn btn-primary waves-effect waves-light d-flex align-items-center shadow-sm"
                          onClick={() => navigate("/account_transaction?type=user")}
                        >
                          <i className="bx bx-history me-2 fs-5"></i> <span>User to User History</span>
                        </button>
                      ) : activeTab === "mt5_to_wallet" ? (
                        <button
                          className="btn btn-primary waves-effect waves-light d-flex align-items-center shadow-sm"
                          onClick={() => navigate("/account_transaction?type=mt5")}
                        >
                          <i className="bx bx-history me-2 fs-5"></i> <span>MT5 to Wallet History</span>
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary waves-effect waves-light d-flex align-items-center shadow-sm"
                          onClick={() => navigate("/account_transaction?type=mt5")}
                        >
                          <i className="bx bx-history me-2 fs-5"></i> <span>Wallet to MT5 History</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Row */}
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-body p-md-4 p-3">
                      {/* Card Header with Fund Wallet pill */}
                      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-3 border-bottom gap-2">
                        <div>
                          <h4 className="card-title mb-0 fs-4 mb-2 fw-bold text-primary">
                            {activeTab === "mt5"
                              ? "Wallet to MT5 Transfer"
                              : activeTab === "mt5_to_wallet"
                                ? "MT5 to Wallet Transfer"
                                : "User to User Transfer"}
                          </h4>
                          <p className="text-muted mb-0">
                            {activeTab === "mt5"
                              ? "Transfer funds from your Fund Wallet to your MT5 Live Account"
                              : activeTab === "mt5_to_wallet"
                                ? "Transfer funds from your MT5 Live Account to your Fund Wallet"
                                : "Transfer funds securely from your Fund Wallet to another registered user"}
                          </p>
                        </div>
                        {userData && (
                          <div className="pe-4 gap-2 ps-3 py-2 flex-grow-1 flex-sm-grow-0 rounded-4 d-flex align-items-center bg-primary bg-opacity-10 ms-auto" style={{ minWidth: 'fit-content' }}>
                            <div className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center shadow-sm" style={{ width: '38px', height: '38px' }}>
                              <i className="bx bx-wallet fs-4"></i>
                            </div>
                            <div>
                              <span className="d-block text-primary fw-bold" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                Income Wallet
                              </span>
                              <span className="fw-bold text-primary" style={{ fontSize: '18px', paddingTop: "2px" }}>
                                ${userData.commissionwallet != null ? Number(userData.commissionwallet).toFixed(2) : '0.00'}
                              </span>
                            </div>
                            {/* <div>
                              <span className="d-block text-primary fw-bold" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                                Fund Wallet
                              </span>
                              <span className="fw-bold text-primary" style={{ fontSize: '18px', paddingTop: "2px" }}>
                                ${userData.pinwallet != null ? Number(userData.pinwallet).toFixed(2) : '0.00'}
                              </span>
                            </div> */}
                          </div>
                        )}
                      </div>

                      {/* Transfer Type Selection Tabs */}
                      <div className="mb-4">
                        <ul className="nav nav-pills bg-light p-1 rounded-3" role="tablist">
                          <li className="nav-item flex-fill text-center" role="presentation">
                            <button
                              type="button"
                              className={`nav-link w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 ${activeTab === 'mt5' ? 'active shadow-sm' : 'text-muted'}`}
                              onClick={() => setActiveTab('mt5')}
                            >
                              <i className="bx bx-transfer-alt fs-5"></i>
                              <span>Wallet to MT5 Account</span>
                            </button>
                          </li>
                          {/* MT5 to Wallet tab hidden for now - uncomment to enable again */}
                          {/* <li className="nav-item flex-fill text-center" role="presentation">
                            <button
                              type="button"
                              className={`nav-link w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 ${activeTab === 'mt5_to_wallet' ? 'active shadow-sm' : 'text-muted'}`}
                              onClick={() => setActiveTab('mt5_to_wallet')}
                            >
                              <i className="bx bx-wallet-alt fs-5"></i>
                              <span>MT5 to Wallet</span>
                            </button>
                          </li> */}
                          {ENABLE_USER_TO_USER_TRANSFER && (
                            <li className="nav-item flex-fill text-center" role="presentation">
                              <button
                                type="button"
                                className={`nav-link w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 ${activeTab === 'user' ? 'active shadow-sm' : 'text-muted'}`}
                                onClick={() => setActiveTab('user')}
                              >
                                <i className="bx bx-user-check fs-5"></i>
                                <span>User to User Transfer</span>
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* TAB 1: Wallet to MT5 Transfer Form */}
                      {activeTab === "mt5" && (
                        <div>
                          {/* Warning when user has no MT5 account */}
                          {!loadingAccounts && accounts.length === 0 && (
                            <div className="alert alert-warning d-flex align-items-center mb-4 py-3 px-3 rounded-3" role="alert">
                              <i className="bx bx-info-circle fs-3 me-2 text-warning"></i>
                              <div>
                                <strong>No MT5 Live Account found!</strong> <span>You must have an active MT5 Live account to transfer funds. </span>
                                <Link to="/live_account" className="alert-link text-decoration-underline fw-bold">
                                  Create a Live Account here
                                </Link>
                                .
                              </div>
                            </div>
                          )}

                          <form onSubmit={handleBeforeMt5Transfer} noValidate>
                            <div className="row">
                              {/* Transfer Type (Account Selection) */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">Transfer Type</label>
                                {loadingAccounts ? (
                                  <div className="form-control px-3 py-2 bg-light text-muted">
                                    <span className="spinner-border spinner-border-sm me-2"></span><span>Loading accounts...</span>
                                  </div>
                                ) : accounts.length > 0 ? (
                                  <select
                                    className={`form-select px-3 py-2 ${mt5Errors.account_number ? 'is-invalid' : ''}`}
                                    name="account_number"
                                    value={mt5FormData.account_number}
                                    onChange={handleMt5InputChange}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {accounts.map((acc) => (
                                      <option key={acc} value={acc}>
                                        Wallet to MT5 Account ({acc})
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="form-control px-3 py-2 bg-light text-muted d-flex align-items-center justify-content-between">
                                    <span className="text-danger fw-medium">No Account Found</span>
                                    <Link to="/live_account" className="badge bg-primary text-white text-decoration-none py-1 px-2">
                                      + Create MT5
                                    </Link>
                                  </div>
                                )}
                                {mt5Errors.account_number && (
                                  <small className="text-danger mt-1 d-block">{mt5Errors.account_number}</small>
                                )}
                              </div>

                              {/* MT5 Account Balance */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold mb-1">MT5 Account Balance</label>
                                <div
                                  className="form-control px-3 py-2 bg-light fw-bold text-dark"
                                  style={{ cursor: 'not-allowed' }}
                                >
                                  {loadingAccounts
                                    ? 'Loading accounts...'
                                    : accounts.length === 0
                                      ? 'No Account Found'
                                      : loadingBalance
                                        ? 'Loading balance...'
                                        : `$${balance !== null ? Number(balance).toFixed(2) : '0.00'}`}
                                </div>
                              </div>

                              {/* Amount */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">Amount</label>
                                <div className="input-group">
                                  <span className="input-group-text fw-bold bg-light text-muted">$</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className={`form-control px-3 py-2 ${mt5Errors.amount ? 'is-invalid' : ''}`}
                                    name="amount"
                                    placeholder="0.00"
                                    autoComplete="off"
                                    maxLength="20"
                                    value={mt5FormData.amount}
                                    onKeyDown={(e) => {
                                      if (["e", "E", "+", "-"].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onChange={handleMt5InputChange}
                                  />
                                </div>
                                {mt5Errors.amount && <small className="text-danger mt-1 d-block">{mt5Errors.amount}</small>}
                                <small className="text-muted mt-1 d-block">Min Transfer: $100 | Max: $10,000,000</small>
                              </div>

                              {/* MT5 Master Password */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">MT5 Trading Password</label>
                                <div className="input-group">
                                  <input
                                    type={showMt5Password ? "text" : "password"}
                                    className={`form-control px-3 py-2 ${mt5Errors.password ? 'is-invalid' : ''}`}
                                    name="password"
                                    placeholder="Enter your MT5 trading password"
                                    autoComplete="new-password"
                                    value={mt5FormData.password}
                                    onChange={handleMt5InputChange}
                                  />
                                  <button
                                    className="btn btn-outline-secondary border"
                                    type="button"
                                    onClick={() => setShowMt5Password(!showMt5Password)}
                                  >
                                    <i className={showMt5Password ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"} />
                                  </button>
                                </div>
                                {mt5Errors.password && <small className="text-danger mt-1 d-block">{mt5Errors.password}</small>}
                              </div>

                              {/* Submit Button */}
                              <div className="col-12 text-end mt-2">
                                <button
                                  type="submit"
                                  className="btn btn-primary px-4 py-2 fw-medium shadow-sm"
                                  disabled={loading || accounts.length === 0}
                                >
                                  {loading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2"></span><span>Processing...</span>
                                    </>
                                  ) : (
                                    "Submit Transfer"
                                  )}
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}

                      {/* TAB 2: MT5 to Wallet Transfer Form */}
                      {activeTab === "mt5_to_wallet" && (
                        <div>
                          {/* Warning when user has no MT5 account */}
                          {!loadingAccounts && accounts.length === 0 && (
                            <div className="alert alert-warning d-flex align-items-center mb-4 py-3 px-3 rounded-3" role="alert">
                              <i className="bx bx-info-circle fs-3 me-2 text-warning"></i>
                              <div>
                                <strong>No MT5 Live Account found!</strong> <span>You must have an active MT5 Live account to transfer funds to your wallet. </span>
                                <Link to="/live_account" className="alert-link text-decoration-underline fw-bold">
                                  Create a Live Account here
                                </Link>
                                .
                              </div>
                            </div>
                          )}

                          <form onSubmit={handleBeforeMt5ToWalletTransfer} noValidate>
                            <div className="row">
                              {/* Source MT5 Account Selection */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">MT5 Live Account (From)</label>
                                {loadingAccounts ? (
                                  <div className="form-control px-3 py-2 bg-light text-muted">
                                    <span className="spinner-border spinner-border-sm me-2"></span><span>Loading accounts...</span>
                                  </div>
                                ) : accounts.length > 0 ? (
                                  <select
                                    className={`form-select px-3 py-2 ${mt5ToWalletErrors.account_number ? 'is-invalid' : ''}`}
                                    name="account_number"
                                    value={mt5ToWalletFormData.account_number}
                                    onChange={handleMt5ToWalletInputChange}
                                  >
                                    {accounts.map((acc) => (
                                      <option key={acc} value={acc}>
                                        MT5 Live Account ({acc})
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="form-control px-3 py-2 bg-light text-muted d-flex align-items-center justify-content-between">
                                    <span className="text-danger fw-medium">No Account Found</span>
                                    <Link to="/live_account" className="badge bg-primary text-white text-decoration-none py-1 px-2">
                                      + Create MT5
                                    </Link>
                                  </div>
                                )}
                                {mt5ToWalletErrors.account_number && (
                                  <small className="text-danger mt-1 d-block">{mt5ToWalletErrors.account_number}</small>
                                )}
                              </div>

                              {/* Available MT5 Balance */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold mb-1">MT5 Account Balance (Available)</label>
                                <div
                                  className="form-control px-3 py-2 bg-light fw-bold text-dark"
                                  style={{ cursor: 'not-allowed' }}
                                >
                                  {loadingAccounts
                                    ? 'Loading accounts...'
                                    : accounts.length === 0
                                      ? 'No Account Found'
                                      : loadingMt5ToWalletBalance
                                        ? 'Loading balance...'
                                        : `$${mt5ToWalletBalance !== null ? Number(mt5ToWalletBalance).toFixed(2) : '0.00'}`}
                                </div>
                              </div>

                              {/* Amount */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">Amount to Transfer <span className="text-danger">*</span></label>
                                <div className="input-group">
                                  <span className="input-group-text fw-bold bg-light text-muted">$</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className={`form-control px-3 py-2 ${mt5ToWalletErrors.amount ? 'is-invalid' : ''}`}
                                    name="amount"
                                    placeholder="0.00"
                                    autoComplete="off"
                                    maxLength="20"
                                    value={mt5ToWalletFormData.amount}
                                    onKeyDown={(e) => {
                                      if (["e", "E", "+", "-"].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onChange={handleMt5ToWalletInputChange}
                                  />
                                </div>
                                {mt5ToWalletErrors.amount && <small className="text-danger mt-1 d-block">{mt5ToWalletErrors.amount}</small>}
                                <small className="text-muted mt-1 d-block">Min Transfer: $1 | Max: $10,000,000</small>
                              </div>

                              {/* MT5 Master Password */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">MT5 Trading Password <span className="text-danger">*</span></label>
                                <div className="input-group">
                                  <input
                                    type={showMt5ToWalletPassword ? "text" : "password"}
                                    className={`form-control px-3 py-2 ${mt5ToWalletErrors.password ? 'is-invalid' : ''}`}
                                    name="password"
                                    placeholder="Enter your MT5 trading password"
                                    autoComplete="new-password"
                                    value={mt5ToWalletFormData.password}
                                    onChange={handleMt5ToWalletInputChange}
                                  />
                                  <button
                                    className="btn btn-outline-secondary border"
                                    type="button"
                                    onClick={() => setShowMt5ToWalletPassword(!showMt5ToWalletPassword)}
                                  >
                                    <i className={showMt5ToWalletPassword ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"} />
                                  </button>
                                </div>
                                {mt5ToWalletErrors.password && <small className="text-danger mt-1 d-block">{mt5ToWalletErrors.password}</small>}
                              </div>

                              {/* Destination Info Box */}
                              <div className="col-12 mb-3">
                                <div className="p-3 bg-light rounded-3 d-flex align-items-center justify-content-between border">
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-primary-subtle text-primary p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                      <i className="bx bx-wallet fs-4"></i>
                                    </div>
                                    <div>
                                      <span className="text-muted small d-block">Destination Account</span>
                                      <strong className="text-dark">Fund Wallet ({userData?.email || 'Your Wallet'})</strong>
                                    </div>
                                  </div>
                                  <div className="text-end">
                                    <span className="text-muted small d-block">Current Fund Wallet Balance</span>
                                    <span className="fw-bold text-success">${userData?.pinwallet != null ? Number(userData.pinwallet).toFixed(2) : '0.00'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Submit Button */}
                              <div className="col-12 text-end mt-2">
                                <button
                                  type="submit"
                                  className="btn btn-primary px-4 py-2 fw-medium shadow-sm"
                                  disabled={loading || accounts.length === 0}
                                >
                                  {loading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2"></span><span>Processing...</span>
                                    </>
                                  ) : (
                                    "Submit Transfer"
                                  )}
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}

                      {/* TAB 3: User to User Transfer Form (CAN BE COMMENTED OUT IF NEEDED) */}
                      {ENABLE_USER_TO_USER_TRANSFER && activeTab === "user" && (
                        <div>
                          <form onSubmit={handleBeforeUserTransfer} noValidate>
                            <div className="row">
                              {/* Receiver Email */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">
                                  Receiver Email <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <span className="input-group-text bg-light text-muted">
                                    <i className="bx bx-envelope"></i>
                                  </span>
                                  <input
                                    type="email"
                                    className={`form-control px-3 py-2 ${userErrors.receiver_email ? 'is-invalid' : ''}`}
                                    name="receiver_email"
                                    placeholder="Enter receiver's registered email"
                                    value={userFormData.receiver_email}
                                    onChange={handleUserInputChange}
                                    autoComplete="off"
                                  />
                                </div>
                                {userErrors.receiver_email && (
                                  <small className="text-danger mt-1 d-block">{userErrors.receiver_email}</small>
                                )}
                              </div>

                              {/* Amount */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">
                                  Amount <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <span className="input-group-text fw-bold bg-light text-muted">$</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className={`form-control px-3 py-2 ${userErrors.amount ? 'is-invalid' : ''}`}
                                    name="amount"
                                    placeholder="0.00"
                                    autoComplete="off"
                                    maxLength="20"
                                    value={userFormData.amount}
                                    onKeyDown={(e) => {
                                      if (["e", "E", "+", "-"].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onChange={handleUserInputChange}
                                  />
                                </div>
                                {userErrors.amount && <small className="text-danger mt-1 d-block">{userErrors.amount}</small>}
                                <small className="text-muted mt-1 d-block">Min Transfer: $10 | Max: $10,000,000</small>
                              </div>

                              {/* Account Password */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">
                                  Account Password <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type={showUserPassword ? "text" : "password"}
                                    className={`form-control px-3 py-2 ${userErrors.password ? 'is-invalid' : ''}`}
                                    name="password"
                                    placeholder="Enter your account password"
                                    autoComplete="new-password"
                                    value={userFormData.password}
                                    onChange={handleUserInputChange}
                                  />
                                  <button
                                    className="btn btn-outline-secondary border"
                                    type="button"
                                    onClick={() => setShowUserPassword(!showUserPassword)}
                                  >
                                    <i className={showUserPassword ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"} />
                                  </button>
                                </div>
                                {userErrors.password && <small className="text-danger mt-1 d-block">{userErrors.password}</small>}
                              </div>

                              {/* Description / Remarks */}
                              <div className="col-md-6 mb-4">
                                <label className="form-label text-muted fw-semibold">Description (Optional)</label>
                                <input
                                  type="text"
                                  className="form-control px-3 py-2"
                                  name="description"
                                  placeholder="Enter transfer remark (optional)"
                                  value={userFormData.description}
                                  onChange={handleUserInputChange}
                                />
                              </div>

                              {/* Submit Button */}
                              <div className="col-12 text-end mt-2">
                                <button
                                  type="submit"
                                  className="btn btn-primary px-4 py-2 fw-medium shadow-sm"
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2"></span><span>Processing...</span>
                                    </>
                                  ) : (
                                    "Submit Transfer"
                                  )}
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Transfer Details Card */}
                <div className="col-xl-4 mt-4 mt-lg-0">
                  <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-body p-4">
                      <h4 className="card-title mb-4 fw-bold text-dark">Transfer Details</h4>
                      <div className="table-responsive">
                        <CommonTable
                          hideHeader={true}
                          columns={[
                            { accessor: "label", render: (row) => <th className="text-muted fw-semibold py-3">{row.label}</th> },
                            { accessor: "value", className: "bg-light-subtle fw-bold py-3 text-dark", render: (row) => row.value }
                          ]}
                          data={[
                            {
                              label: "Transfer Mode",
                              value:
                                activeTab === "mt5"
                                  ? "Wallet to MT5 Live"
                                  : activeTab === "mt5_to_wallet"
                                    ? "MT5 Live to Wallet"
                                    : "User to User"
                            },
                            {
                              label: "Minimum Transfer",
                              value:
                                activeTab === "mt5"
                                  ? "$100"
                                  : activeTab === "mt5_to_wallet"
                                    ? "$1"
                                    : "$10"
                            },
                            { label: "Maximum Transfer", value: "$10,000,000" },
                            { label: "Fee / Commission", value: "0.00% (Free)" },
                            { label: "Processing Time", value: "Instant" }
                          ]}
                          tableClassName="table border align-middle mb-0 table-nowrap"
                          disablePagination={true}
                        />
                      </div>

                      <div className="mt-4 p-3 bg-light rounded-3 text-muted small">
                        <div className="d-flex align-items-start">
                          <i className="bx bx-shield-quarter text-primary fs-4 me-2 mt-1"></i>
                          <div>
                            <strong className="text-dark">Secure Verification</strong>
                            <p className="mb-0 mt-1">
                              All transfers require 6-digit email OTP verification for top-grade account security and fund safety.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* OTP Modal Overlay (Unified for both MT5 & User to User Transfers) */}
      <div id="otp-modal-container">
        {showOtpModal && (
          <>
            <div className="modal-backdrop fade show" style={{ backdropFilter: 'blur(4px)' }}></div>
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
              data-bs-backdrop="static"
              data-bs-keyboard="false"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content overflow-hidden">
                  <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
                    <h5 className="modal-title">Security Verification</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowOtpModal(false)}
                      disabled={otpLoading}
                    ></button>
                  </div>
                  <div className="modal-body p-4 text-center">
                    <div
                      className="mb-4 text-primary bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: '80px', height: '80px' }}
                    >
                      <i className="bx bx-shield-quarter" style={{ fontSize: '48px' }}></i>
                    </div>
                    <p className="text-muted mb-4 px-3">
                      Please enter the 6-digit verification code sent to your registered email to securely confirm this transfer.
                    </p>

                    <div className="d-flex justify-content-center gap-2 mb-4">
                      {[0, 1, 2, 3, 4, 5].map((index) => {
                        return (
                          <input
                            key={index}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            className="form-control text-center fw-bold text-primary shadow-sm"
                            style={{
                              width: "45px",
                              height: "55px",
                              fontSize: "24px",
                              borderRadius: "12px",
                              border: "2px solid #e0e0e0",
                              transition: "all 0.2s"
                            }}
                            value={otp[index] || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (!/^[0-9]*$/.test(val)) return;

                              let newOtpArray = otp.split("");
                              newOtpArray[index] = val.substring(val.length - 1);
                              setOtp(newOtpArray.join(""));

                              if (val && e.target.nextElementSibling) {
                                e.target.nextElementSibling.focus();
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Backspace") {
                                if (!otp[index] && e.target.previousElementSibling) {
                                  e.target.previousElementSibling.focus();
                                } else if (otp[index]) {
                                  let newOtpArray = otp.split("");
                                  newOtpArray[index] = "";
                                  setOtp(newOtpArray.join(""));
                                }
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pastedData = e.clipboardData.getData("text/plain").trim();
                              if (!/^[0-9]+$/.test(pastedData)) return;

                              const pastedChars = pastedData.substring(0, 6).split("");
                              let newOtpArray = Array(6).fill("");

                              for (let i = 0; i < pastedChars.length; i++) {
                                newOtpArray[i] = pastedChars[i];
                              }
                              setOtp(newOtpArray.join(""));

                              const inputs = e.target.parentElement.querySelectorAll('input');
                              const targetIndex = Math.min(5, pastedChars.length);
                              if (inputs[targetIndex] && pastedChars.length < 6) {
                                inputs[targetIndex].focus();
                              } else if (inputs[5]) {
                                inputs[5].focus();
                              }
                            }}
                            onFocus={(e) => e.target.style.borderColor = "var(--color-primary)"}
                            onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                          />
                        );
                      })}
                    </div>

                    <button
                      className="btn btn-primary w-100 py-3 fw-bold shadow-sm rounded-3 fs-5 mt-2"
                      onClick={handleConfirmTransfer}
                      disabled={otp.length !== 6 || otpLoading}
                      style={{ transition: 'all 0.3s' }}
                    >
                      {otpLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span><span>Verifying...</span>
                        </>
                      ) : (
                        "Confirm Transfer"
                      )}
                    </button>

                    <div className="mt-4">
                      {timeLeft > 0 ? (
                        <p className="text-muted mb-0">
                          Resend OTP in <span className="fw-bold text-primary">{formatTime(timeLeft)}</span>
                        </p>
                      ) : (
                        <button
                          className="btn btn-link text-primary fw-bold text-decoration-none p-0"
                          onClick={handleResendOtp}
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
